/**
 * Yataş Grup · AI Visibility — paylaşım sayfası (25 Eyl 2026).
 *
 * platform.glowytics.com/p/<gizli-slug> — sidebar YOK, marka seçici YOK, üstte
 * Glowytics + Yataş Grup logoları. İçerik panelin /ai-visibility sayfasının AYNISI
 * (aynı bileşen, publicSlug ile) → iki sayfa asla ayrışmaz.
 *
 * Panel hesabı gerekmez ama linkin KENDİ kullanıcı adı/şifresi var: giriş
 * POST /api/ai-visibility/public/<slug>/login → 30 günlük anahtar (tarayıcıda saklanır),
 * veri uçları onsuz 401. Kimlik DB'de (projects.settings.publicAccess); değiştirmek için
 * glowytics-backend/scripts/ai_visibility_share_password.js. Slug config/publicReportLinks.js'te;
 * backend'deki PUBLIC_LINKS ile aynı olmak ZORUNDA.
 */
import React, { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import YatasAiVisibility from '../YatasAiVisibility';
import { YATAS_AI_VISIBILITY_SLUG } from '../../config/publicReportLinks';

const API = 'https://api.glowytics.com';
const TOKEN_KEY = `aiv-share:${YATAS_AI_VISIBILITY_SLUG}`;
const readToken = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const writeToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* gizli sekme */ } };

function LoginCard({ onToken, expired }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(expired ? 'Oturumunuzun süresi doldu, lütfen tekrar giriş yapın.' : null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API}/api/ai-visibility/public/${YATAS_AI_VISIBILITY_SLUG}/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, password }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.token) throw new Error(j.error || 'Giriş yapılamadı.');
      onToken(j.token);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  return (
    <div className="flex justify-center pt-10 sm:pt-20">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-gray-200 rounded-3xl shadow-sm p-6 sm:p-8 space-y-5">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center"><Lock className="w-5 h-5" /></span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">AI Visibility</h1>
            <p className="text-sm text-gray-500">Devam etmek için giriş yapın.</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="aiv-user" className="text-sm font-medium text-gray-700">Kullanıcı adı</label>
          <input id="aiv-user" autoComplete="username" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} required
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="aiv-pass" className="text-sm font-medium text-gray-700">Şifre</label>
          <input id="aiv-pass" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <button type="submit" disabled={busy}
          className="w-full rounded-xl bg-blue-600 text-white text-sm font-semibold py-2.5 hover:bg-blue-700 disabled:opacity-60">
          {busy ? 'Giriş yapılıyor…' : 'Giriş yap'}
        </button>
      </form>
    </div>
  );
}

export default function YatasAiVisibilityPublic() {
  const [token, setToken] = useState(readToken);
  const [expired, setExpired] = useState(false);

  // Arama motoru indekslemesin; tıklanan dış siteye (kaynak linkleri) adres sızmasın.
  useEffect(() => {
    document.title = 'Yataş Grup · AI Visibility';
    const tags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive' },
      { name: 'referrer', content: 'no-referrer' },
    ].map(({ name, content }) => {
      const el = document.createElement('meta');
      el.setAttribute('name', name);
      el.setAttribute('content', content);
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach((el) => el.remove());
  }, []);

  const onToken = (t) => { writeToken(t); setExpired(false); setToken(t); };
  const logout = (isExpired = false) => { writeToken(null); setExpired(isExpired); setToken(null); };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          <img src="/Calisma-Yuzeyi-20@5x-4.png" alt="Glowytics" className="h-7 w-auto" />
          <div className="flex items-center gap-4">
            {token && (
              <button type="button" onClick={() => logout(false)} className="text-xs text-gray-500 hover:text-gray-800">Çıkış yap</button>
            )}
            <img src="/yatas-grup-logo.png" alt="Yataş Grup" className="h-8 w-auto" />
          </div>
        </div>
      </header>
      <main className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {token
          ? <YatasAiVisibility publicSlug={YATAS_AI_VISIBILITY_SLUG} publicToken={token} onAuthError={() => logout(true)} />
          : <LoginCard onToken={onToken} expired={expired} />}
      </main>
    </div>
  );
}
